import { useEffect, useRef, useState } from "react";
import { trackEvent } from "@/hooks/useEventTracking";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { ShelterWithDistance } from "@/pages/Index";
import { isUrgentNeedsActive } from "@/types/shelter";
import { Button } from "@/components/ui/button";
import { Locate, Loader2, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { useCookieConsent } from "@/hooks/useCookieConsent";

interface ShelterMapProps {
  shelters: ShelterWithDistance[];
  selectedShelterId: string | null;
  onShelterSelect: (shelter: ShelterWithDistance) => void;
  onClearSelection?: () => void;
  userLocation: [number, number] | null;
  onUserLocationChange: (location: [number, number] | null) => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  focusCity?: string;
}

// Fix for default marker icons in leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const createMarkerIcon = (isUrgent: boolean) => {
  return L.divIcon({
    html: `
      <div style="
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${isUrgent ? 'hsl(15, 90%, 55%)' : 'hsl(155, 45%, 35%)'};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 3px 8px rgba(0,0,0,0.3);
      ">
        <svg 
          style="transform: rotate(45deg); width: 16px; height: 16px; color: white;"
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
    `,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// User location marker
const createUserLocationIcon = () => {
  return L.divIcon({
    html: `
      <div style="
        width: 20px;
        height: 20px;
        background: hsl(210, 100%, 50%);
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      "></div>
    `,
    className: "",
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
};

export const ShelterMap = ({ 
  shelters, 
  selectedShelterId, 
  onShelterSelect,
  onClearSelection,
  userLocation,
  onUserLocationChange,
  isFullscreen = false,
  onToggleFullscreen,
  focusCity
}: ShelterMapProps) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const lastClickedFromMapRef = useRef(false);
  const [isLocating, setIsLocating] = useState(false);
  const { hasConsent } = useCookieConsent();

  // Poland center coordinates and bounds
  const defaultCenter: [number, number] = [52.0, 19.0];
  const defaultZoom = 7;
  
  // Poland bounds with padding (SW corner, NE corner)
  const polandBounds: L.LatLngBoundsExpression = [
    [48.5, 13.5], // Southwest (with small padding)
    [55.5, 24.5], // Northeast (with small padding)
  ];

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = L.map(mapContainerRef.current, {
      maxBounds: polandBounds,
      maxBoundsViscosity: 1.0,
      minZoom: 6,
      maxZoom: 18,
    }).setView(defaultCenter, defaultZoom);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(mapRef.current);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Update markers when shelters change
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Filter out shelters without proper address (only city name or empty)
    const sheltersWithAddress = shelters.filter(shelter => {
      if (!shelter.address) return false;
      const addressLower = shelter.address.toLowerCase().trim();
      const cityLower = shelter.city.toLowerCase().trim();
      // Exclude if address is empty, equals city name, or is just city with country
      if (addressLower === '' || addressLower === cityLower) return false;
      if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
      return true;
    });

    // Add new markers only for shelters with proper addresses
    sheltersWithAddress.forEach((shelter) => {
      const distanceText = shelter.distance !== undefined 
        ? `<p style="font-size: 12px; color: #0066cc; font-weight: 500; margin-bottom: 8px;">📍 ${formatDistance(shelter.distance)} od Ciebie</p>`
        : '';

      const isUrgent = isUrgentNeedsActive(shelter);

      const marker = L.marker([shelter.lat, shelter.lng], {
        icon: createMarkerIcon(isUrgent),
        title: shelter.name,
        alt: `Schronisko: ${shelter.name}`,
      }).addTo(mapRef.current!);
      
      // Add aria-label to marker element for screen readers after it's added to DOM
      marker.on('add', () => {
        const markerElement = marker.getElement();
        if (markerElement) {
          markerElement.setAttribute('aria-label', `Schronisko ${shelter.name}, ${shelter.city}`);
        }
      });
      
      // Also try to set immediately in case 'add' already fired
      const markerElement = marker.getElement();
      if (markerElement) {
        markerElement.setAttribute('aria-label', `Schronisko ${shelter.name}, ${shelter.city}`);
      }

      // Add tooltip for hover
      marker.bindTooltip(shelter.name, {
        permanent: false,
        direction: 'top',
        offset: [0, -30],
        className: 'shelter-tooltip'
      });

      const popupContent = `
        <div style="padding: 12px; min-width: 220px;">
          <h3 style="font-weight: 600; font-size: 16px; margin-bottom: 4px;">
            ${shelter.name}
          </h3>
          <p style="font-size: 14px; color: #666; margin-bottom: 8px;">${shelter.city}</p>
          ${distanceText}
          
          <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;">
            ${isUrgent ? `
              <span style="display: inline-flex; align-items: center; gap: 4px; background: #f97316; color: white; padding: 2px 8px; border-radius: 9999px; font-size: 12px; font-weight: 500;">
                ⚠️ Pilne
              </span>
            ` : ''}
            ${shelter.dog_walking_available ? `
              <span style="display: inline-flex; align-items: center; gap: 4px; background: #10b981; color: white; padding: 2px 8px; border-radius: 9999px; font-size: 12px; font-weight: 500;">
                🐕 Spacery
              </span>
            ` : ''}
          </div>

          ${shelter.current_needs ? `
            <div style="margin-bottom: 8px;">
              <p style="font-size: 11px; font-weight: 500; color: #888; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px;">
                Czego potrzebują
              </p>
              <p style="font-size: 14px; color: #444; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${shelter.current_needs}</p>
            </div>
          ` : ''}

          <button
            onclick="window.dispatchEvent(new CustomEvent('shelter-select', { detail: '${shelter.id}' }))"
            style="width: 100%; padding: 6px 12px; background: #059669; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer;"
          >
            Zobacz szczegóły
          </button>
        </div>
      `;

      marker.bindPopup(popupContent);
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        lastClickedFromMapRef.current = true;
        trackEvent("Otworzono schronisko", { shelter_id: shelter.id, shelter_name: shelter.name, shelter_city: shelter.city, source: "map" });
        onShelterSelect(shelter);
      });
      markersRef.current.push(marker);
    });
  }, [shelters, onShelterSelect]);

  // Sync user marker with userLocation prop and center map
  useEffect(() => {
    if (!mapRef.current) return;

    // Remove previous user marker
    if (userMarkerRef.current) {
      userMarkerRef.current.remove();
      userMarkerRef.current = null;
    }

    // Add new user marker if location exists
    if (userLocation) {
      userMarkerRef.current = L.marker(userLocation, {
        icon: createUserLocationIcon(),
        zIndexOffset: 1000,
        title: 'Twoja lokalizacja',
        alt: 'Twoja lokalizacja',
      }).addTo(mapRef.current);
      
      // Add aria-label to user marker for screen readers
      const userMarkerElement = userMarkerRef.current.getElement();
      if (userMarkerElement) {
        userMarkerElement.setAttribute('aria-label', 'Twoja lokalizacja na mapie');
      }

      userMarkerRef.current.bindPopup(`
        <div style="padding: 8px; text-align: center;">
          <p style="font-weight: 600; margin-bottom: 4px;">📍 Twoja lokalizacja</p>
          <p style="font-size: 12px; color: #666;">Lista posortowana wg odległości</p>
        </div>
      `);

      // Center map on user location with ~30km radius (zoom 10)
      mapRef.current.flyTo(userLocation, 10, { duration: 1 });
    }
  }, [userLocation]);

  // Center map on selected shelter - only when triggered from list, not from map click
  useEffect(() => {
    if (!mapRef.current || !selectedShelterId) return;
    
    // Skip flyTo if the selection was triggered by clicking a marker on the map
    if (lastClickedFromMapRef.current) {
      lastClickedFromMapRef.current = false;
      return;
    }

    const selectedShelter = shelters.find(s => s.id === selectedShelterId);
    if (selectedShelter) {
      mapRef.current.flyTo([selectedShelter.lat, selectedShelter.lng], 12, { duration: 0.5 });
    }
  }, [selectedShelterId, shelters]);

  // Center map on selected city, filtered shelters, or reset to Poland view
  useEffect(() => {
    if (!mapRef.current) return;
    
    // Skip if we have a selected shelter (let the shelter selection effect handle it)
    if (selectedShelterId) return;

    // Skip if user location is active (user location effect handles centering)
    if (userLocation) return;

    // If city filter is active, center on that city's shelters
    if (focusCity) {
      const cityShelters = shelters.filter(s => s.city === focusCity);
      if (cityShelters.length === 0) return;

      if (cityShelters.length === 1) {
        mapRef.current.flyTo([cityShelters[0].lat, cityShelters[0].lng], 12, { duration: 0.5 });
      } else {
        const bounds = L.latLngBounds(cityShelters.map(s => [s.lat, s.lng] as [number, number]));
        mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 0.5 });
      }
      return;
    }

    // If search resulted in a small number of shelters (1-5), auto-center on them
    if (shelters.length > 0 && shelters.length <= 5) {
      if (shelters.length === 1) {
        mapRef.current.flyTo([shelters[0].lat, shelters[0].lng], 12, { duration: 0.5 });
      } else {
        const bounds = L.latLngBounds(shelters.map(s => [s.lat, s.lng] as [number, number]));
        mapRef.current.flyToBounds(bounds, { padding: [50, 50], duration: 0.5 });
      }
      return;
    }

    // No filters active - reset to default Poland view
    mapRef.current.flyTo(defaultCenter, defaultZoom, { duration: 0.5 });
  }, [focusCity, shelters, selectedShelterId, userLocation]);

  // Handle geolocation
  const handleLocateUser = () => {
    if (!hasConsent) {
      toast.error("Musisz zaakceptować cookies, aby korzystać z geolokalizacji");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Twoja przeglądarka nie obsługuje geolokalizacji");
      return;
    }

    setIsLocating(true);

    // Detect iOS/iPadOS for specific error messages
    const ua = navigator.userAgent;
    const isIOS =
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes("Macintosh") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
    
    const showGeolocationError = (error: GeolocationPositionError) => {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          // Show a shorter toast for the map button - the main detailed popup is in Index.tsx
          toast.error(
            isIOS 
              ? "Lokalizacja zablokowana. Użyj przycisku lokalizacji na liście schronisk, aby zobaczyć instrukcje."
              : "Brak dostępu do lokalizacji. Włącz w ustawieniach przeglądarki i odśwież.",
            { duration: 6000 }
          );
          break;
        case error.POSITION_UNAVAILABLE:
          if (isIOS) {
            toast.error("Nie udało się określić lokalizacji. Sprawdź czy Usługi lokalizacji są włączone: Ustawienia → Prywatność → Usługi lokalizacji.", {
              duration: 10000,
            });
          } else {
            toast.error("Nie udało się określić lokalizacji. Sprawdź czy GPS/lokalizacja jest włączona.");
          }
          break;
        case error.TIMEOUT:
          toast.error("Upłynął czas oczekiwania na lokalizację. Upewnij się, że masz włączoną lokalizację i spróbuj ponownie.");
          break;
        default:
          toast.error("Błąd podczas pobierania lokalizacji");
      }
    };

    // iOS Safari requires specific handling - try with high accuracy first, then fallback
    const handleSuccess = (position: GeolocationPosition) => {
      const { latitude, longitude } = position.coords;
      
      // Check if location is in Poland bounds
      if (latitude < 48.5 || latitude > 55.5 || longitude < 13.5 || longitude > 24.5) {
        toast.error("Twoja lokalizacja jest poza granicami Polski");
        setIsLocating(false);
        return;
      }

      // Clear any selected shelter so the map doesn't try to re-center on it
      if (onClearSelection) {
        onClearSelection();
      }

      onUserLocationChange([latitude, longitude]);

      // Fly to user location with zoom level 10 (~30km radius visible)
      if (mapRef.current) {
        mapRef.current.flyTo([latitude, longitude], 10, { duration: 1.5 });
      }
      
      toast.success("Mapa wyśrodkowana na Twojej lokalizacji!");
      setIsLocating(false);
    };

    const handleError = (error: GeolocationPositionError) => {
      // On iOS, if high accuracy fails, try without it
      if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          (fallbackError) => {
            setIsLocating(false);
            showGeolocationError(fallbackError);
          },
          {
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 600000, // 10 minutes cache
          }
        );
        return;
      }
      
      setIsLocating(false);
      showGeolocationError(error);
    };

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  };

  // Invalidate map size when fullscreen changes
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 100);
    }
  }, [isFullscreen]);

  return (
    <div className="relative h-full w-full">
      <div 
        ref={mapContainerRef}
        className="h-full w-full rounded-xl overflow-hidden shadow-elevated"
      />
      
      {/* Control buttons */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
        {/* Fullscreen toggle button */}
        {onToggleFullscreen && (
          <Button
            onClick={onToggleFullscreen}
            size="icon"
            variant="secondary"
            className="shadow-lg bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
            title={isFullscreen ? "Zamknij pełny ekran" : "Powiększ mapę"}
          >
            {isFullscreen ? (
              <Minimize2 className="w-5 h-5" />
            ) : (
              <Maximize2 className="w-5 h-5" />
            )}
          </Button>
        )}
        
        {/* Geolocation button */}
        <Button
          onClick={handleLocateUser}
          disabled={isLocating}
          size="icon"
          variant="secondary"
          className="shadow-lg bg-white text-gray-700 border border-gray-200 hover:bg-gray-100"
          title="Znajdź moją lokalizację"
        >
          {isLocating ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Locate className="w-5 h-5" />
          )}
        </Button>
      </div>
    </div>
  );
};
